import { SortOrderEnum, TextValue, TextValueString } from '@shared/types';
import { orderBy } from 'lodash-es';
import { TableFilterPill } from '../table-filter-pill/table-filter-pill.interface';
import { TableSortPill } from '../table-sort-pill/table-sort-pill.interface';
import { PillDataType, TableFilterPillConfig } from './table-filter-pill-config.interface';
import { TableColumn } from './ui-table-config.interface';

// Overload for complex values (returns numbers)
function composeValues<T>(items: T[], source: { value: T; text: string }[]): { label: string; value: number }[];
// Overload for string values (returns strings)
function composeValues(items: string[], source: string[]): { label: string; value: string }[];
// Overload for TextValue / TextValueString (label + value from selected object)
function composeValues(
    items: object[],
    source: TextValue[] | TextValueString[]
): { label: string; value: number | string }[];

function composeValues<T>(
    items: T[] | string[] | object[],
    source: { value: T; text: string }[] | string[] | TextValue[] | TextValueString[]
): { label: string; value: number | string }[] {
    return items.map(item => {
        if (Array.isArray(source) && typeof source[0] === 'string') {
            // Handle string array case
            const stringSource = source as string[];
            return {
                value: item as string,
                label: stringSource.find(entry => entry === item) ?? '',
            };
        } else if (
            typeof item === 'object' &&
            item !== null &&
            'text' in item &&
            'value' in item &&
            typeof source[0] === 'object' &&
            source[0] !== null
        ) {
            // Basic + isObject: item is TextValue | TextValueString (use item, not source[0])
            const o = item as TextValue | TextValueString;
            return {
                value: o.value,
                label: o.text,
            };
        } else {
            // Handle complex object array case
            const complexSource = source as { value: T; text: string }[];
            return {
                value: Number(item),
                label: complexSource.find(entry => entry.value === item)?.text ?? '',
            };
        }
    });
}

function composeDateRangeValue(
    dateRange: (Date | null)[] | null,
    dateFormat = 'MM/DD/YYYY',
    dateRangeMapper?: (startDate: Date, endDate: Date | null) => { startDate: Date; endDate: Date | null }
): { label: string; value: number }[] {
    if (!dateRange || dateRange.length !== 2) {
        return [];
    }

    const [rawStartDate, rawEndDate] = dateRange;

    if (!rawStartDate) {
        return [];
    }

    const mappedDates = dateRangeMapper
        ? dateRangeMapper(rawStartDate, rawEndDate ?? null)
        : { startDate: rawStartDate, endDate: rawEndDate };
    const { startDate, endDate } = mappedDates;

    return [
        {
            label: endDate
                ? `${localTimeToUserTimezone(startDate, dateFormat)} - ${localTimeToUserTimezone(endDate, dateFormat)}`
                : localTimeToUserTimezone(startDate, dateFormat),
            value: -1,
        },
    ];
}

function composeSortPill<T>(columns: TableColumn<T>[]): TableSortPill[] {
    const sortPill: TableSortPill[] = [];
    const sortOperations = columns.filter(c => c.isSortingActive);

    orderBy(sortOperations, ['sortPriority']).forEach(sort => {
        sortPill.push({
            name: sort.name ?? '',
            criterion: Number(sort.field),
            order: sort.sortOrder || SortOrderEnum.ASC,
            numericalSort: sort.numericalSort || false,
        });
    });

    return sortPill;
}

function composeFilterPills<F>(
    filterPillConfigs: TableFilterPillConfig[],
    filters: F,
    filterOptions: any,
    removeSimpleFilter: (field: string, fallback: any) => void,
    removeArrayFilter: (elem: { removeAll: boolean; id: any }, field: string) => void
): { label: string; data: TableFilterPill }[] {
    const filterPills: { label: string; data: TableFilterPill }[] = [];

    filterPillConfigs.forEach(config => {
        const dataKey = config.dataKey as keyof typeof filters;

        // Determine if filter should be shown based on data type and data presence
        let shouldShow = false;
        if (config.dataType === PillDataType.Basic) {
            const value = filters[dataKey];
            if (typeof value === 'boolean') {
                shouldShow = value;
            } else if (typeof value === 'object' && value !== null && config.isObject) {
                shouldShow = Object.values(value).every(prop => prop !== null && prop !== undefined);
            } else {
                shouldShow = value !== undefined && value !== null && value !== '';
            }
        } else if (config.dataType === PillDataType.Complex) {
            const arrayValue = filters[dataKey] as any[];
            shouldShow = arrayValue && arrayValue.length > 0;
        } else if (config.dataType === PillDataType.DateRange) {
            const dateRange = filters[dataKey] as (Date | null)[] | null;
            shouldShow = Boolean(dateRange && dateRange.length === 2 && dateRange[0] !== null);
        }

        if (shouldShow) {
            // Get items and source based on data type
            let items: any[];
            let source: any[];

            if (config.dataType === PillDataType.Basic) {
                if (config.sourceEnum) {
                    // Use sourceEnum for Basic type
                    const value = filters[dataKey] as any;
                    const enumText = config.sourceEnum[value];
                    if (enumText) {
                        items = [enumText];
                        source = [enumText];
                    } else {
                        // Fallback if enum text not found
                        items = [value];
                        source = [value];
                    }
                } else {
                    items = [filters[dataKey]];
                    source = [filters[dataKey]];
                }
            } else if (config.dataType === PillDataType.DateRange) {
                items = (filters[dataKey] as (Date | null)[]) || [];
                source = [];
            } else {
                // Complex type
                items = filters[dataKey] as any[];

                // Get source from filterOptions based on dataKey
                const filterOptionsKey = dataKey as keyof typeof filterOptions;
                const filterOptionsData = filterOptions[filterOptionsKey] as any[];

                if (config.sourceMapper) {
                    // Use sourceMapper if provided
                    source = filterOptionsData.map(config.sourceMapper);
                } else {
                    // Use default mapping
                    source = filterOptionsData;
                }
            }

            const hasOverlay = config.dataType !== PillDataType.DateRange ? items.length > 1 : false;

            let values;
            if (config.dataType === PillDataType.Basic) {
                if (config.isObject) {
                    values = composeValues(items as object[], source as TextValue[] | TextValueString[]);
                } else {
                    values = composeValues(items as string[], source as string[]);
                }
            } else if (config.dataType === PillDataType.DateRange) {
                values = composeDateRangeValue(items as (Date | null)[], config.dateFormat, config.dateRangeMapper);
            } else {
                values = composeValues(items as any[], source as { value: any; text: string }[]);
            }

            // Generate onRemove function based on data type
            let onRemove: (elem?: { removeAll: boolean; id: any }) => void;
            if (config.dataType === PillDataType.Basic) {
                onRemove = () => removeSimpleFilter(dataKey as string, null);
            } else if (config.dataType === PillDataType.DateRange) {
                onRemove = () => removeSimpleFilter(dataKey as string, []);
            } else {
                onRemove = (elem?: { removeAll: boolean; id: any }) => {
                    if (elem) {
                        removeArrayFilter(elem, dataKey as string);
                    }
                };
            }

            filterPills.push({
                label: config.label,
                data: {
                    isRemovable: !config.preventRemoval,
                    hasOverlay,
                    values,
                    onRemove,
                },
            });
        }
    });

    return filterPills;
}

function localTimeToUserTimezone(date: Date, format = 'MMM DD, YYYY'): string {
    const day = date.getDate().toString();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const monthNameShort = date.toLocaleDateString('en-US', { month: 'short' });

    switch (format) {
        case 'DD/MM/YYYY':
            return `${day.padStart(2, '0')}/${month}/${year}`;
        case 'MM/DD/YYYY':
            return `${month}/${day.padStart(2, '0')}/${year}`;
        case 'YYYY/MM/DD':
            return `${year}/${month}/${day.padStart(2, '0')}`;
        case 'MMM DD, YYYY':
            return `${monthNameShort} ${day}, ${year}`;
        case 'MMM YYYY':
            return `${monthNameShort} ${year}`;
    }

    return `${month}/${day.padStart(2, '0')}/${year}`;
}

export const TablePillsHelper = {
    composeSortPill,
    composeFilterPills,
};
